package de.xtl.retroguide.join

import androidx.compose.runtime.Stable
import de.xtl.retroguide.protocol.MAX_ANSWER_LENGTH
import de.xtl.retroguide.protocol.MAX_QUESTION_LENGTH
import de.xtl.retroguide.protocol.MAX_QUESTIONS
import de.xtl.retroguide.protocol.DEFAULT_QUESTION_DURATION_SECONDS
import de.xtl.retroguide.protocol.SESSION_CODE_LENGTH
import de.xtl.retroguide.protocol.SessionAccess
import de.xtl.retroguide.protocol.SessionSnapshot
import de.xtl.retroguide.protocol.ParticipantNameOption
import de.xtl.retroguide.protocol.ParticipantRole
import de.xtl.retroguide.protocol.QuestionWidget
import de.xtl.retroguide.protocol.RetroPhase
import de.xtl.retroguide.protocol.ActionItem
import de.xtl.retroguide.protocol.AnswerCategory
import de.xtl.retroguide.protocol.AnswerCluster
import de.xtl.retroguide.protocol.DEFAULT_VOTES_PER_PARTICIPANT
import de.xtl.retroguide.protocol.MAX_CLUSTER_NAME_LENGTH
import de.xtl.retroguide.protocol.StartRetroQuestion
import de.xtl.retroguide.protocol.collectsTopics
import de.xtl.retroguide.protocol.toActionItem
import de.xtl.retroguide.protocol.toDotVote
import de.xtl.retroguide.protocol.CategorizedAnswer
import de.xtl.retroguide.protocol.VisibleAnswer
import de.xtl.retroguide.protocol.categories
import de.xtl.retroguide.protocol.isValidAnswerFor
import de.xtl.retroguide.protocol.toCategorizedAnswer
import de.xtl.retroguide.protocol.maxAnswersPerParticipant
import de.xtl.retroguide.protocol.ParticipantAnswerChoice

@Stable
data class JoinSessionState(
    val displayName: String = "",
    val selectedParticipantName: String = "",
    val sessionCode: String = "",
    val participantNameOptions: List<ParticipantNameOption> = emptyList(),
    val participantNamesDraft: String = "",
    val isParticipantNamesDirty: Boolean = false,
    val questionDrafts: List<String> = DEFAULT_QUESTIONS,
    val questionTimerDrafts: List<String> = List(DEFAULT_QUESTIONS.size) {
        (DEFAULT_QUESTION_DURATION_SECONDS / 60).toString()
    },
    val questionTimerEnabled: List<Boolean> = List(DEFAULT_QUESTIONS.size) { false },
    val questionWidgets: List<QuestionWidget> = List(DEFAULT_QUESTIONS.size) { QuestionWidget.TEXT },
    val questionAnswerVisibility: List<Boolean> = List(DEFAULT_QUESTIONS.size) { false },
    val questionVoteSources: List<Int?> = List(DEFAULT_QUESTIONS.size) { null },
    val questionVoteBudgets: List<Int> = List(DEFAULT_QUESTIONS.size) { DEFAULT_VOTES_PER_PARTICIPANT },
    val isClustering: Boolean = false,
    val clusterSelection: Set<String> = emptySet(),
    val clusterNameDraft: String = "",
    val currentTimerDraft: String = "",
    val answerDraft: String = "",
    val answerCategoryDraft: AnswerCategory? = null,
    val editingAnswerId: String? = null,
    val questionRemainingSeconds: Int? = null,
    val processedAnswerIds: Set<String> = emptySet(),
    val screen: RetroScreen = RetroScreen.JOIN,
    val isLoading: Boolean = false,
    val isRosterLoading: Boolean = false,
    val showSessionEndedDialog: Boolean = false,
    val error: JoinSessionError? = null,
    val access: SessionAccess? = null,
    val session: SessionSnapshot? = null,
) {
    val invitationUrl: String
        get() = "$PUBLIC_APP_URL/join/$sessionCode"

    val canJoin: Boolean
        get() = !isLoading &&
            sessionCode.length == SESSION_CODE_LENGTH &&
            participantNameOptions.any { it.name == selectedParticipantName && !it.isSelected }

    val canSaveParticipantNames: Boolean
        get() = !isLoading && isParticipantNamesDirty && participantNamesDraft.asParticipantNames().isNotEmpty()

    val canStartRetro: Boolean
        get() {
            val configuredQuestions = questionDrafts.indices.filter { questionDrafts[it].isNotBlank() }
            return !isLoading && session != null && configuredQuestions.isNotEmpty() &&
                configuredQuestions.all { !questionTimerEnabled[it] || questionTimerDrafts[it].validQuestionTimerMinutes() } &&
                configuredQuestions.all { questionWidgets[it] != QuestionWidget.DOT_VOTING || effectiveVoteSource(it) != null }
        }

    val canAddQuestion: Boolean
        get() = !isLoading && questionDrafts.size < MAX_QUESTIONS

    val canStartCurrentQuestionTimer: Boolean
        get() = !isLoading &&
            access?.role == ParticipantRole.MODERATOR &&
            session?.phase == RetroPhase.QUESTION_ACTIVE &&
            session.currentQuestion?.durationSeconds == null &&
            currentTimerDraft.validQuestionTimerMinutes()

    val isAnsweringAllowed: Boolean
        get() = !isLoading &&
            (questionRemainingSeconds == null || questionRemainingSeconds > 0) &&
            session?.isAnsweringAllowed == true &&
            session.ownAnswerChoice == null &&
            session.ownAnswers.size < currentWidget.maxAnswersPerParticipant

    val canSetAnswerChoice: Boolean
        get() = !isLoading &&
            (questionRemainingSeconds == null || questionRemainingSeconds > 0) &&
            session?.isAnsweringAllowed == true

    val canModifyOwnAnswers: Boolean
        get() = !isLoading &&
            (questionRemainingSeconds == null || questionRemainingSeconds > 0) &&
            session?.isAnsweringAllowed == true

    private val currentWidget: QuestionWidget
        get() = session?.currentQuestion?.widget ?: QuestionWidget.TEXT

    val answerContent: String?
        get() = when {
            currentWidget.categories.isNotEmpty() ->
                answerCategoryDraft?.let { CategorizedAnswer(it, answerDraft.trim()).encode() }
            currentWidget == QuestionWidget.ACTION_ITEMS -> answerDraft.toActionItem()
                ?.let { ActionItem(it.what.trim(), it.who.trim(), it.due.trim()).encode() }
            else -> answerDraft.trim()
        }

    private val isVoteWithinOptions: Boolean
        get() = currentWidget != QuestionWidget.DOT_VOTING || answerContent?.toDotVote()?.isValidFor(
            optionIds = session?.votingOptions.orEmpty().map { it.id }.toSet(),
            budget = session?.currentQuestion?.votesPerParticipant ?: DEFAULT_VOTES_PER_PARTICIPANT,
        ) == true

    val canSubmitAnswer: Boolean
        get() = answerContent?.isValidAnswerFor(currentWidget) == true && isVoteWithinOptions &&
            if (editingAnswerId == null) isAnsweringAllowed else canModifyOwnAnswers
}

enum class RetroScreen {
    JOIN,
    MODERATOR_LOBBY,
    PARTICIPANT_LOBBY,
    MODERATOR_QUESTION,
    PARTICIPANT_QUESTION,
    MODERATOR_SUMMARY,
}

enum class JoinSessionError {
    NAME_REQUIRED,
    SESSION_NOT_FOUND,
    CONNECTION,
    ACTION_REJECTED,
    NAME_UNAVAILABLE,
    RATE_LIMITED,
}

sealed interface JoinSessionAction {
    data class OnDisplayNameChange(val value: String) : JoinSessionAction
    data class OnSessionCodeChange(val value: String) : JoinSessionAction
    data class OnParticipantNameSelect(val value: String) : JoinSessionAction
    data class OnParticipantNamesChange(val value: String) : JoinSessionAction
    data class OnQuestionChange(val index: Int, val value: String) : JoinSessionAction
    data class OnQuestionTimerChange(val index: Int, val value: String) : JoinSessionAction
    data class OnQuestionTimerEnabledChange(val index: Int, val enabled: Boolean) : JoinSessionAction
    data class OnQuestionWidgetChange(val index: Int, val widget: QuestionWidget) : JoinSessionAction
    data class OnQuestionAnswerVisibilityChange(val index: Int, val showAll: Boolean) : JoinSessionAction
    data class OnAnswerProcessedChange(val answerId: String, val processed: Boolean) : JoinSessionAction
    data class OnCurrentTimerChange(val value: String) : JoinSessionAction
    data class OnCurrentQuestionAnswerVisibilityChange(val showAll: Boolean) : JoinSessionAction
    data class OnAnswerChange(val value: String) : JoinSessionAction
    data class OnAnswerCategoryChange(val category: AnswerCategory) : JoinSessionAction
    data class OnQuestionVoteSourceChange(val index: Int, val source: Int) : JoinSessionAction
    data class OnQuestionVoteBudgetChange(val index: Int, val budget: Int) : JoinSessionAction
    data class OnClusterModeChange(val enabled: Boolean) : JoinSessionAction
    data class OnClusterAnswerToggle(val answerId: String) : JoinSessionAction
    data class OnClusterNameChange(val value: String) : JoinSessionAction
    data object OnCreateClusterClick : JoinSessionAction
    data class OnDeleteClusterClick(val clusterId: String) : JoinSessionAction
    data class OnAnswerChoiceChange(val choice: ParticipantAnswerChoice?) : JoinSessionAction
    data class OnEditAnswerClick(val answerId: String) : JoinSessionAction
    data class OnDeleteAnswerClick(val answerId: String) : JoinSessionAction
    data object OnCancelAnswerEditClick : JoinSessionAction
    data object OnJoinClick : JoinSessionAction
    data object OnCreateRetroClick : JoinSessionAction
    data object OnAddQuestionClick : JoinSessionAction
    data object OnSaveParticipantNamesClick : JoinSessionAction
    data object OnStartRetroClick : JoinSessionAction
    data object OnStartCurrentQuestionTimerClick : JoinSessionAction
    data object OnSubmitAnswerClick : JoinSessionAction
    data object OnRevealAnswersClick : JoinSessionAction
    data object OnNextQuestionClick : JoinSessionAction
    data object OnDownloadRetroClick : JoinSessionAction
    data object OnShareRetroClick : JoinSessionAction
    data object OnLeaveLobbyClick : JoinSessionAction
    data object OnSummaryDoneClick : JoinSessionAction
    data object OnSessionEndedConfirmClick : JoinSessionAction
}

sealed interface JoinSessionEvent {
    data class DownloadMarkdown(val fileName: String, val content: String) : JoinSessionEvent
    data class ShareMarkdown(val fileName: String, val content: String) : JoinSessionEvent
}

fun String.asQuestionDraft() = take(MAX_QUESTION_LENGTH)

fun String.asQuestionTimerDraft() = filter(Char::isDigit).take(2)

fun String.validQuestionTimerMinutes() = toIntOrNull() in 1..60

fun String.asAnswerDraft() = take(MAX_ANSWER_LENGTH)

/** Earlier, non-blank questions that collect topics and can feed the dot voting at [index]. */
fun JoinSessionState.votingSourceCandidates(index: Int): List<Int> = (0 until index).filter {
    questionDrafts[it].isNotBlank() && questionWidgets[it].collectsTopics
}

fun JoinSessionState.effectiveVoteSource(index: Int): Int? {
    val candidates = votingSourceCandidates(index)
    return questionVoteSources.getOrNull(index)?.takeIf { it in candidates } ?: candidates.lastOrNull()
}

/** Builds the start request; voting sources are re-indexed because blank questions are not sent. */
fun JoinSessionState.startRetroQuestions(): List<StartRetroQuestion> {
    val configured = questionDrafts.indices.filter { questionDrafts[it].isNotBlank() }
    return configured.map { index ->
        val widget = questionWidgets[index]
        StartRetroQuestion(
            text = questionDrafts[index],
            durationSeconds = questionTimerDrafts[index].toIntOrNull()?.times(60).takeIf { questionTimerEnabled[index] },
            widget = widget,
            showAllParticipantAnswers = questionAnswerVisibility[index],
            sourceQuestionIndex = effectiveVoteSource(index)
                ?.let(configured::indexOf)
                ?.takeIf { widget == QuestionWidget.DOT_VOTING },
            votesPerParticipant = questionVoteBudgets.getOrElse(index) { DEFAULT_VOTES_PER_PARTICIPANT },
        )
    }
}

/** Current clusters plus a new one from the selection; selected answers leave their old clusters. */
fun JoinSessionState.clustersWithNewCluster(id: String): List<AnswerCluster>? {
    val name = clusterNameDraft.trim()
    val session = session ?: return null
    if (name.isEmpty() || clusterSelection.isEmpty()) return null
    val ordered = session.visibleAnswers.map { it.id }.filter { it in clusterSelection }
    return session.clusters
        .map { it.copy(answerIds = it.answerIds - clusterSelection) }
        .filter { it.answerIds.isNotEmpty() } + AnswerCluster(id, name.take(MAX_CLUSTER_NAME_LENGTH), ordered)
}

fun List<AnswerCluster>.without(clusterId: String) = filterNot { it.id == clusterId }

internal fun JoinSessionState.editing(answer: VisibleAnswer): JoinSessionState {
    val categorized = answer.content.takeIf {
        session?.currentQuestion?.widget?.categories?.isNotEmpty() == true
    }?.toCategorizedAnswer()
    return copy(
        editingAnswerId = answer.id,
        answerDraft = categorized?.text ?: answer.content,
        answerCategoryDraft = categorized?.category ?: answerCategoryDraft,
        error = null,
    )
}

val QuestionWidget.defaultQuestion: String?
    get() = when (this) {
        QuestionWidget.SAILBOAT -> "Wie lief unsere Fahrt im letzten Sprint?"
        QuestionWidget.MAD_SAD_GLAD -> "Was hat dich im letzten Sprint bewegt?"
        QuestionWidget.STARFISH -> "Was sollten wir anfangen, lassen, beibehalten, mehr oder weniger tun?"
        QuestionWidget.FOUR_L -> "Wie blickst du auf den letzten Sprint zurück?"
        QuestionWidget.ROTI -> "Wie viel hat dir diese Retro gebracht?"
        QuestionWidget.WEATHER -> "Welches Wetter beschreibt deine Stimmung?"
        QuestionWidget.BATTERY -> "Wie voll ist deine Batterie gerade?"
        QuestionWidget.HOT_AIR_BALLOON -> "Was hat uns im letzten Sprint getragen und was runtergezogen?"
        QuestionWidget.SPEEDCAR -> "Was hat uns im letzten Sprint schnell gemacht und was gebremst?"
        QuestionWidget.KALM -> "Was behalten wir, was fügen wir hinzu, wovon weniger, wovon mehr?"
        QuestionWidget.DAKI -> "Was lassen wir weg, was fügen wir hinzu, was behalten wir, was verbessern wir?"
        QuestionWidget.TEAM_RADAR -> "Wie steht unser Team gerade da?"
        QuestionWidget.DOT_VOTING -> "Welche Themen sind uns am wichtigsten?"
        QuestionWidget.ACTION_ITEMS -> "Welche Maßnahmen nehmen wir uns vor?"
        QuestionWidget.TEXT -> null
    }

fun String.asSessionCode() = filter(Char::isLetterOrDigit)
    .uppercase()
    .take(SESSION_CODE_LENGTH)

fun String.invitationSessionCode(): String? {
    val parts = trim('/').split('/')
    val code = parts.getOrNull(1).orEmpty()
    return code.uppercase().takeIf {
        parts.size == 2 && parts.first() == "join" && it.length == SESSION_CODE_LENGTH && it.all(Char::isLetterOrDigit)
    }
}

fun String.asParticipantNames() = split(',', '\n')
    .map(String::trim)
    .filter(String::isNotEmpty)

private val DEFAULT_QUESTIONS = listOf(
    "Was lief in diesem Sprint gut?",
    "Was lief in diesem Sprint nicht gut?",
    "Was möchten wir im nächsten Sprint verbessern?",
)

private const val PUBLIC_APP_URL = "https://retro.xtl.dev"
